import { AnimatePresence } from "framer-motion";
import React, { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import Login from "./Pages/login";
import ForgotPassword from "./Pages/login/ForgotPassword";
import { checkAuth } from "./firebase/authService";

import LocationAutocomplete from "./Components/LocationAutocomplete";
import Preloader from "./Components/Preloader";
import NotFoundPage from "./Pages/404";
import AddCarouselItem from "./Pages/AdminDashboard/HeroSection/Carousels/AddCarouselItem";
import CarouselList from "./Pages/AdminDashboard/HeroSection/Carousels/CarouselList";
import EditCarouselItem from "./Pages/AdminDashboard/HeroSection/Carousels/EditCarouselItem";
import HeroConfiguration from "./Pages/AdminDashboard/HeroSection/HeroConfig/HeroConfiguration";
import AddImportantInformation from "./Pages/AdminDashboard/Important_information/AddImportantInformation";
import EditImportantInformation from "./Pages/AdminDashboard/Important_information/EditImportantInformation";
import EditImportantInformationHeader from "./Pages/AdminDashboard/Important_information/EditImportantInformationHeader";
import ImportantInformationHeaderList from "./Pages/AdminDashboard/Important_information/ImportantInformationHeaderList";
import ImportantInformationList from "./Pages/AdminDashboard/Important_information/ImportantInformationList";
import AddInformationCard from "./Pages/AdminDashboard/InformationCard/AddInformationCard";
import EditInformationCard from "./Pages/AdminDashboard/InformationCard/EditInformationCard";
import InformationCardList from "./Pages/AdminDashboard/InformationCard/InformationCardList";
import EventsHeaderList from "./Pages/AdminDashboard/InformationCard/EventsHeaderList";
import EditEventsHeader from "./Pages/AdminDashboard/InformationCard/EditEventsHeader";
import AddMonthlyRepresentation from "./Pages/AdminDashboard/Representation/MonthlyRepresentation/AddMonthlyRepresentation";
import AddRepresentationDate from "./Pages/AdminDashboard/Representation/MonthlyRepresentation/AddRepresentationDate";
import AddRepresentative from "./Pages/AdminDashboard/Representation/MonthlyRepresentation/AddRepresentative";
import EditRepresentationDate from "./Pages/AdminDashboard/Representation/MonthlyRepresentation/EditRepresentationDate";
import EditRepresentative from "./Pages/AdminDashboard/Representation/MonthlyRepresentation/EditRepresentative";
import RepresentationData from "./Pages/AdminDashboard/Representation/MonthlyRepresentation/RepresentationData";
import RepresentationDates from "./Pages/AdminDashboard/Representation/MonthlyRepresentation/RepresentationDates";
import RepresentativeList from "./Pages/AdminDashboard/Representation/MonthlyRepresentation/RepresentativeList";
import EventRegistrationsList from "./Pages/AdminDashboard/EventRegistrations/EventRegistrationsList";
import SocialLinks from "./Pages/AdminDashboard/SocialLinks";
import SubscriberList from "./Pages/AdminDashboard/SuscriberList";
import AboutList from "./Pages/AdminDashboard/about/AboutList";
import EditAboutItem from "./Pages/AdminDashboard/about/EditAboutItem";
import Addblog from "./Pages/AdminDashboard/blogs/Addblog";
import BlogView from "./Pages/AdminDashboard/blogs/BlogView";
import Editblog from "./Pages/AdminDashboard/blogs/Editblog";
import AddFormBody from "./Pages/AdminDashboard/form/AddFormBody";
import EditFormBody from "./Pages/AdminDashboard/form/EditFormBody";
import EditFormHeader from "./Pages/AdminDashboard/form/EditFormHeader";
import FormBodyList from "./Pages/AdminDashboard/form/FormBodyList";
import FormHeaderList from "./Pages/AdminDashboard/form/FormHeaderList";
import AddGalleryItem from "./Pages/AdminDashboard/gallery/AddGalleryItem";
import Categories from "./Pages/AdminDashboard/gallery/Categories";
import EditGalleryHeader from "./Pages/AdminDashboard/gallery/EditGalleryHeader";
import EditGalleryItem from "./Pages/AdminDashboard/gallery/EditGalleryItem";
import GalleryBodyList from "./Pages/AdminDashboard/gallery/GalleryBodyList";
import GalleryHeaderList from "./Pages/AdminDashboard/gallery/GalleryHeaderList";
import AddService from "./Pages/AdminDashboard/services/AddService";
import AddServiceHeader from "./Pages/AdminDashboard/services/AddServiceHeader";
import EditService from "./Pages/AdminDashboard/services/EditService";
import EditServiceHeader from "./Pages/AdminDashboard/services/EditServiceHeader";
import ServiceHeaderList from "./Pages/AdminDashboard/services/ServiceHeaderList";
import ServicesList from "./Pages/AdminDashboard/services/ServicesList";
import AddTeamMember from "./Pages/AdminDashboard/team/AddTeamMember";
import EditTeamHeader from "./Pages/AdminDashboard/team/EditTeamHeader";
import EditTeamMember from "./Pages/AdminDashboard/team/EditTeamMember";
import TeamBodyList from "./Pages/AdminDashboard/team/TeamBodyList";
import TeamHeaderList from "./Pages/AdminDashboard/team/TeamHeaderList";
import AddWeeklyRepresentation from "./Pages/AdminDashboard/weekly_representation/AddWeeklyRepresentation";
import EditWeeklyRepresentation from "./Pages/AdminDashboard/weekly_representation/EditWeeklyRepresentation";
import EditWeeklyRepresentationHeader from "./Pages/AdminDashboard/weekly_representation/EditWeeklyRepresentationHeader";
import WeeklyRepresentationHeaderList from "./Pages/AdminDashboard/weekly_representation/WeeklyRepresentationHeaderList";
import WeeklyRepresentationList from "./Pages/AdminDashboard/weekly_representation/WeeklyRepresentationList";
import ResetPassword from "./Pages/login/ResetPassword";
// Landing page Imports 
import ContactInfo from "./Pages/AdminDashboard/ContactInfo";
import ContactUsClassicPage from "./Pages/Contact/ContactUsClassicPage";
import ContactUsBackground from "./Pages/AdminDashboard/PageBackgrounds/ContactUsBackground";
import ToDosBackground from "./Pages/AdminDashboard/PageBackgrounds/ToDosBackground";
import AdminLandingDashboard from "./Pages/AdminDashboard/AdminLandingDashboard";

const ContactSubmissions = lazy(() => import("./Pages/AdminDashboard/ContactSubmissions"));
import HomeStartupPage from "./Pages/Home/Startup/Startup";
import TeamDetail from "./Pages/TeamDetail";

import WichtigeInfo from "./Pages/WichtigeInfo/WichtigeInfo";

const Privacy = lazy(() => import("./Pages/Privacy"));

const BlogSimplePage = lazy(() => import("./Pages/Blogs/BlogSimple"));

const BlogStandardPostPage = lazy(() =>
  import("./Pages/Blogs/PostTypes/BlogStandardPostPage")
);

const EventRegistration = lazy(() =>
  import("./Pages/EventRegistration/EventRegistration")
);



const ProtectedRoute = ({ element }) => {
  const [isAuth, setIsAuth] = useState(null);

  useEffect(() => {
    const authenticate = async () => {
      const authStatus = await checkAuth();
      setIsAuth(authStatus);
    };

    authenticate();
  }, []);

  if (isAuth === null) {
    return <Preloader />;
  }

  return isAuth ? element : <Navigate to="/login" />;
};

const DashboardRoutes = () => (
  <AnimatePresence mode="wait">
    <Suspense fallback={<></>}>

      <Routes>

        {/* Landing page Routes  */}
        <Route
          path="/"
          element={<HomeStartupPage style={{ "--base-color": "#faede0" }} />}
        />

        <Route path="/wichtigeinfo" element={<WichtigeInfo />} />

        <Route path="/allnews" element={<BlogSimplePage />} />

        <Route path="blogdetail">
          <Route
            path=":id"
            element={
              <BlogStandardPostPage style={{ "--base-color": "#faede0" }} />
            }
          />
        </Route>
        <Route
          path="/contactus"
          element={<ContactUsClassicPage style={{ "--base-color": "#faede0" }} />}
        />
        <Route
          path="/privacy"
          element={<Privacy style={{ "--base-color": "#faede0" }} />}
        />
        <Route
          path="/teamdetail"
          element={<TeamDetail />}
        />
        <Route
          path="/event-registration/:eventId"
          element={<EventRegistration />}
        />



        {/* Dashboard Routes  */}





        {/* Admin Landing Dashboard */}
        <Route path="/admin-dashboard" element={<ProtectedRoute element={<AdminLandingDashboard />} />} />

        <Route path="/login" element={<Login />} />
        <Route path="/forgotpassword" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />




        {/* Hero Section */}
        {/* Hero Configuration */}
        <Route path="/heroconfig" element={<ProtectedRoute element={<HeroConfiguration />} />} />
        {/* Hero Carousel */}
        <Route path="/herocarousel" element={<ProtectedRoute element={<CarouselList />} />} />
        <Route path="/herocarousel/addherocarousel" element={<ProtectedRoute element={<AddCarouselItem />} />} />
        <Route path="/herocarousel/editherocarousel/:id" element={<ProtectedRoute element={<EditCarouselItem />} />} />

        {/* InformationCard */}
        <Route path="/informationcard" element={<ProtectedRoute element={<InformationCardList />} />} />
        <Route path="/informationcard/addinformationcard" element={<ProtectedRoute element={<AddInformationCard />} />} />
        <Route path="/informationcard/editinformationcard/:id" element={<ProtectedRoute element={<EditInformationCard />} />} />
        {/* Events Header */}
        <Route path="/eventsheader" element={<ProtectedRoute element={<EventsHeaderList />} />} />
        <Route path="/eventsheader/editeventsheader/:id" element={<ProtectedRoute element={<EditEventsHeader />} />} />

        {/* About list */}
        <Route path="/aboutlist" element={<ProtectedRoute element={<AboutList />} />} />
        <Route path="/aboutlist/editaboutitem/:id" element={<ProtectedRoute element={<EditAboutItem />} />} />

        {/* Services */}
        <Route path="/serviceslist" element={<ProtectedRoute element={<ServicesList />} />} />
        <Route path="/serviceslist/editservice/:id" element={<ProtectedRoute element={<EditService />} />} />
        <Route path="/serviceslist/addservice" element={<ProtectedRoute element={<AddService />} />} />

        {/* Service Header */}
        <Route path="/serviceheader" element={<ProtectedRoute element={<ServiceHeaderList />} />} />
        <Route path="/serviceheader/editserviceheader/:id" element={<ProtectedRoute element={<EditServiceHeader />} />} />
        <Route path="/serviceheader/addserviceheader" element={<ProtectedRoute element={<AddServiceHeader />} />} />

        {/* Team Header */}
        <Route path="/teamheader" element={<ProtectedRoute element={<TeamHeaderList />} />} />
        <Route path="/teamheader/editteamheader/:id" element={<ProtectedRoute element={<EditTeamHeader />} />} />

        {/* Team Body */}
        <Route path="/teamlist" element={<ProtectedRoute element={<TeamBodyList />} />} />
        <Route path="/teamlist/editteammember/:id" element={<ProtectedRoute element={<EditTeamMember />} />} />
        <Route path="/teamlist/addteammember" element={<ProtectedRoute element={<AddTeamMember />} />} />


        {/* Representation */}
        {/* Monthly Representation */}
        <Route path="/monthlyrepresentation/addmonthlyrepresentation" element={<ProtectedRoute element={<AddMonthlyRepresentation />} />} />
        <Route path="/representationdates" element={<ProtectedRoute element={<RepresentationDates />} />} />
        <Route path="/representationdates/addrepresentationdate" element={<ProtectedRoute element={<AddRepresentationDate />} />} />
        <Route path="/representationdates/editrepresentationdate/:id" element={<ProtectedRoute element={<EditRepresentationDate />} />} />

        <Route path="/representationdates/:id/representatives" element={<ProtectedRoute element={<RepresentativeList />} />} />
        <Route path="/representationdates/:id/representatives/addrepresentative" element={<ProtectedRoute element={<AddRepresentative />} />} />
        <Route path="/representationdates/:id/representatives/editrepresentative/:repId" element={<ProtectedRoute element={<EditRepresentative />} />} />

        <Route path="representationdata" element={<ProtectedRoute element={<RepresentationData />} />} />

        {/* Location Autocomplete Component */}
        <Route path="/locationautocomplete" element={<ProtectedRoute element={<LocationAutocomplete />} />} />

        {/* Blogs */}
        <Route path="/blogview" element={<ProtectedRoute element={<BlogView />} />} />
        <Route path="/blogview/addblog" element={<ProtectedRoute element={<Addblog />} />} />
        <Route path="/blogview/edit/:id" element={<ProtectedRoute element={<Editblog />} />} />


        {/* Form Header */}
        <Route path="/formheader" element={<ProtectedRoute element={<FormHeaderList />} />} />
        <Route path="/formheader/editformheader/:id" element={<ProtectedRoute element={<EditFormHeader />} />} />

        {/* Form Body */}
        <Route path="/formbody" element={<ProtectedRoute element={<FormBodyList />} />} />
        <Route path="/formbody/addformbody" element={<ProtectedRoute element={<AddFormBody />} />} />
        <Route path="/formbody/editformbody/:id" element={<ProtectedRoute element={<EditFormBody />} />} />

        {/* Page Backgrounds */}
        <Route path="/contactus-background" element={<ProtectedRoute element={<ContactUsBackground />} />} />
        <Route path="/todos-background" element={<ProtectedRoute element={<ToDosBackground />} />} />

        {/* Weekly Representation  */}

        <Route path="/weeklyrepresentationheader" element={<ProtectedRoute element={<WeeklyRepresentationHeaderList />} />} />
        <Route path="/weeklyrepresentationheader/editweeklyrepresentationheader/:id" element={<ProtectedRoute element={<EditWeeklyRepresentationHeader />} />} />

        {/* Weekly Representation Body  */}
        <Route path="/weeklyrepresentationbody" element={<ProtectedRoute element={<WeeklyRepresentationList />} />} />
        <Route path="/weeklyrepresentationbody/addweeklyrepresentation" element={<ProtectedRoute element={<AddWeeklyRepresentation />} />} />
        <Route path="/weeklyrepresentationbody/editweeklyrepresentation/:id" element={<ProtectedRoute element={<EditWeeklyRepresentation />} />} />

        {/* Important Information  */}

        <Route path="/importantinformationheader" element={<ProtectedRoute element={<ImportantInformationHeaderList />} />} />
        <Route path="/importantinformationheader/editimportantinformationheader/:id" element={<ProtectedRoute element={<EditImportantInformationHeader />} />} />

        <Route path="/importantinformationbody" element={<ProtectedRoute element={<ImportantInformationList />} />} />
        <Route path="/importantinformationbody/addimportantinformation" element={<ProtectedRoute element={<AddImportantInformation />} />} />
        <Route path="/importantinformationbody/editimportantinformation/:id" element={<ProtectedRoute element={<EditImportantInformation />} />} />

        {/* Social Links  */}

        <Route path="/sociallinks" element={<ProtectedRoute element={<SocialLinks />} />} />


        {/* Suscribers */}

        <Route path="/subscribers" element={<ProtectedRoute element={<SubscriberList />} />} />

        {/* Event Registrations */}

        <Route path="/event-registrations" element={<ProtectedRoute element={<EventRegistrationsList />} />} />

        {/* Categories  */}

        <Route path="/categories" element={<ProtectedRoute element={<Categories />} />} />

        {/* Gallery  */}

        <Route path="/galleryheader" element={<ProtectedRoute element={<GalleryHeaderList />} />} />
        <Route path="/galleryheader/editgalleryheader/:id" element={<ProtectedRoute element={<EditGalleryHeader />} />} />

        <Route path="/gallerylist" element={<ProtectedRoute element={<GalleryBodyList />} />} />
        <Route path="/gallerylist/addgalleryitem" element={<ProtectedRoute element={<AddGalleryItem />} />} />
        <Route path="/gallerylist/editgalleryitem/:id" element={<ProtectedRoute element={<EditGalleryItem />} />} />

        <Route path="/contactinfo" element={<ProtectedRoute element={<ContactInfo />} />} />
        <Route path="/contactsubmissions" element={<ProtectedRoute element={<ContactSubmissions />} />} />


        <Route path="*" element={<NotFoundPage />} />

      </Routes>
    </Suspense>
  </AnimatePresence>
);

export default DashboardRoutes;
